import { useState, useEffect } from 'react';
import useDebouncedValue from 'react-scale/hooks/useDebouncedValue';
import { Flex, Autocomplete, TextField, Icon, Spinner } from '@scale/scaleui-radix';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { useSearchTeams } from 'react-scale/corp/views/CorpProjectGroupManager/communication/apiClient';
import { useLookupWorkerTeamsById } from 'react-scale/components/WebinarSessions/api';
import { IListItem } from 'react-scale/components/WebinarSessions/types';
import _ from 'lodash';

interface WorkerTeamAutocompleteProps {
  workerTeamIds: string[];
  onWorkerTeamIdsChange: (workerTeamIds: string[]) => void;
}

export const WorkerTeamAutocomplete = ({
  workerTeamIds,
  onWorkerTeamIdsChange,
}: WorkerTeamAutocompleteProps) => {
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearchInput = useDebouncedValue(searchInput, 500);
  const [fetchedWorkerTeams, setFetchedWorkerTeams] = useState<Record<string, string>>({});

  // Fetches the worker teams to show up as options based on the search input
  const { data: workerTeamData, isFetching: isFetchingWorkerTeamData } = useSearchTeams({
    searchText: debouncedSearchInput,
  });
  const workerTeamOptions = workerTeamData?.map((workerTeam: { id: string; name: string }) => ({
    value: workerTeam.id,
    label: workerTeam.name,
  }));

  // Fetches the names for the currently selected worker teams
  const workerTeamIdsToFetch = workerTeamIds.filter(id => !fetchedWorkerTeams[id]);
  const { data: newWorkerTeams, isFetching: isFetchingNewWorkerTeams } = useLookupWorkerTeamsById({
    workerTeamIds: workerTeamIdsToFetch,
  });

  useEffect(() => {
    if (newWorkerTeams && Object.keys(newWorkerTeams).length > 0) {
      const workerTeamNamesById: Record<string, string> = newWorkerTeams.reduce(
        (acc, item: IListItem) => {
          acc[item.id] = item.name;
          return acc;
        },
        {} as Record<string, string>,
      );
      setFetchedWorkerTeams(prev => ({
        ...prev,
        ...workerTeamNamesById,
      }));
    }
  }, [newWorkerTeams]);

  // TODO: Once Radix is migrated to 1.36, we can use the AsyncItems and AsyncValues to replace the loading state
  return (
    <Autocomplete.Root
      multiple
      search={searchInput}
      onSearchChange={setSearchInput}
      value={workerTeamIds}
      onValueChange={(newWorkerTeamIds: string[]) => {
        if (!_.isEqual(newWorkerTeamIds, workerTeamIds)) {
          onWorkerTeamIdsChange(newWorkerTeamIds);
        }
      }}
    >
      <Autocomplete.Content>
        <Autocomplete.TextField
          placeholder="Search by worker team name..."
          // Do not allow invalid values from paste
          onPaste={() => {}}
        >
          <TextField.Slot side="left">
            <Icon icon={faSearch} label="Search" />
          </TextField.Slot>
          <Autocomplete.Values>
            {workerTeamIds.map(workerTeamId => (
              <Autocomplete.Value key={workerTeamId} value={workerTeamId}>
                {isFetchingNewWorkerTeams && !fetchedWorkerTeams[workerTeamId] ? (
                  <Spinner />
                ) : (
                  fetchedWorkerTeams[workerTeamId]
                )}
              </Autocomplete.Value>
            ))}
          </Autocomplete.Values>
          <Autocomplete.Reset />
        </Autocomplete.TextField>

        <Autocomplete.Items>
          {isFetchingWorkerTeamData
            ? [
                <Autocomplete.Item
                  key={'fetching-worker-teams'}
                  keywords={searchInput}
                  value={searchInput}
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  <Flex direction="row" gap="2">
                    <Spinner />
                    {'Fetching worker teams...'}
                  </Flex>
                </Autocomplete.Item>,
              ]
            : (workerTeamOptions ?? []).map(workerTeam => (
                <Autocomplete.Item key={workerTeam.value} value={workerTeam.value}>
                  {workerTeam.label}
                </Autocomplete.Item>
              ))}
        </Autocomplete.Items>
      </Autocomplete.Content>
    </Autocomplete.Root>
  );
};
